import _ from 'lodash';
import Messages from './Messages';
import ValidRanges from './ValidRanges';

const EMAIL_REGEX =
  /^[a-zA-Z0-9.+_\-!#$%&'*\/=?^`{|}~]+@((?:[\w-]+\.)*\w[\w-]{0,255})\.([a-z]{2,63}(?:\.[a-z]{2})?)$/i;

// according to NTZA, a driver licence number “needs to be two letters followed by six numbers”
const NZ_DRIVER_LICENSE_NUMBER_REGEX = /^[a-zA-Z]{2}[0-9]{6}$/;

// from the Equifax API documentation: travel-document-number is the document number printed on the travel document
// (e.g. passport number). Mandatory. Valid characters are alphanumeric. As specified by the DIA, currently a valid
// travel document number must be no more than 8 characters and formatted as 1 or 2 alphabetic characters followed by
// 6 numeric characters. E.g. LA931026 or L931026
const NZ_PASSPORT_NUMBER_REGEX = /^[a-zA-Z]{1,2}[0-9]{6}$/;

const NAME_REGEX = /^[^<>]+$/;

const validateRequiredField = (field, values) => {
  const ret = {};
  if (!_.get(values, field.name)) {
    _.set(ret, field.name, field.errorMessage);
  }
  return ret;
};

const getDateFields = (values, root, dateFieldName) => {
  const day = _.get(values, `${root}.${dateFieldName}Day`);
  const month = _.get(values, `${root}.${dateFieldName}Month`);
  const year = _.get(values, `${root}.${dateFieldName}Year`);
  return { day, month, year };
};

const isTrueDate = (dateFields) => {
  const storedMonth = Number.parseInt(dateFields.month, 10) - 1;
  const date = new Date(dateFields.year, storedMonth, dateFields.day);
  return (
    date.getFullYear() === Number.parseInt(dateFields.year, 10) &&
    date.getMonth() === storedMonth &&
    date.getDate() === Number.parseInt(dateFields.day, 10)
  );
};

const buildDate = (dateFields, buildOnlyValid) => {
  if (buildOnlyValid
      && !_.isEmpty(dateFields)
      && !_.values(dateFields).some(val => _.isEmpty(val))
      && !isTrueDate(dateFields)) {
    throw new Error(Messages.INVALID_DATE);
  }
  return new Date(dateFields.year, dateFields.month - 1, dateFields.day);
};

const isEmptyOrZero = value => (value == null || value.length === 0 || value === '0');

const isValidDay = value => (!isNaN(value) && value >= ValidRanges.MIN_DAY && value <= ValidRanges.MAX_DAY);

const isValidMonth = value => (!isNaN(value) && value >= ValidRanges.MIN_MONTH && value <= ValidRanges.MAX_MONTH);

const isValidYear = value => (!isNaN(value) && value >= ValidRanges.MIN_YEAR && value <= ValidRanges.MAX_YEAR);

const validateDate = (values, root, dateFieldName) => {
  const dateFields = getDateFields(values, root, dateFieldName);
  const errors = {};
  if (dateFields.day && !isValidDay(dateFields.day)) {
    _.set(errors, `${root}.${dateFieldName}Day`, Messages.REQUIRED_DAY);
  }
  if (dateFields.month && !isValidMonth(dateFields.month)) {
    _.set(errors, `${root}.${dateFieldName}Month`, Messages.REQUIRED_MONTH);
  }
  if (dateFields.year && !isValidYear(dateFields.year)) {
    _.set(errors, `${root}.${dateFieldName}Year`, Messages.REQUIRED_YEAR);
  }
  try {
    buildDate(dateFields, true);
  } catch (e) {
    if (_.isEmpty(errors)) {
      _.set(errors, `${root}.${dateFieldName}Day`, e.message);
    }
  }
  return errors;
};

const ValidationUtils = {
  validateRequiredField,

  validateRequiredFields: (fields, values) => {
    let errors = {};
    for (const field of fields) {
      errors = _.merge(errors, validateRequiredField(field, values));
    }
    return errors;
  },

  validatePastMonthYear: (values, root, dateFieldName) => {
    const dateFields = getDateFields(values, root, dateFieldName);
    dateFields.day = 1;
    const errors = {};
    if (dateFields.month && !dateFields.year) {
      _.set(errors, `${root}.${dateFieldName}Year`, Messages.REQUIRED_YEAR);
    }
    if (!dateFields.month && dateFields.year) {
      _.set(errors, `${root}.${dateFieldName}Month`, Messages.REQUIRED_MONTH);
    }
    if (dateFields.month && !isValidMonth(dateFields.month)) {
      _.set(errors, `${root}.${dateFieldName}Month`, Messages.REQUIRED_MONTH);
    }
    if (dateFields.year && !isValidYear(dateFields.year)) {
      _.set(errors, `${root}.${dateFieldName}Year`, Messages.REQUIRED_YEAR);
    }
    const date = buildDate(dateFields, false);
    if (_.isEmpty(errors) && date.getTime() >= Date.now()) {
      _.set(errors, `${root}.${dateFieldName}Year`, Messages.INVALID_DATE_MUST_BE_PAST);
    }
    return errors;
  },

  validatePastDate: (values, root, dateFieldName) => {
    const errors = validateDate(values, root, dateFieldName);
    const dateFields = getDateFields(values, root, dateFieldName);
    const date = buildDate(dateFields, false);
    if (_.isEmpty(errors) && date.getTime() >= Date.now()) {
      _.set(errors, `${root}.${dateFieldName}Day`, Messages.INVALID_DATE_MUST_BE_PAST);
    }
    return errors;
  },

  validateFutureDate: (values, root, dateFieldName) => {
    const errors = validateDate(values, root, dateFieldName);
    const dateFields = getDateFields(values, root, dateFieldName);
    const date = buildDate(dateFields, false);
    if (_.isEmpty(errors) && date.getTime() <= Date.now()) {
      _.set(errors, `${root}.${dateFieldName}Day`, Messages.INVALID_DATE_MUST_BE_FUTURE);
    }
    return errors;
  },

  validateOverAge: (values, root, dateFieldName, age, dateToValidate) => {
    const errors = validateDate(values, root, dateFieldName);
    const dateFields = getDateFields(values, root, dateFieldName);
    const date = buildDate(dateFields, false);
    date.setFullYear(date.getFullYear() + age);
    const truncateHours = new Date((dateToValidate || new Date()).getTime());
    truncateHours.setHours(0, 0, 0, 0);
    if (_.isEmpty(errors) && truncateHours.getTime() < date.getTime()) {
      _.set(errors, `${root}.${dateFieldName}Day`, `${Messages.INVALID_AGE_MUST_BE_OVER}${age}`);
    }
    return errors;
  },

  validateNzDriverLicenseNumber: (values, fieldName) => {
    const errors = {};
    const licenseNumber = _.get(values, fieldName);
    if (licenseNumber && !NZ_DRIVER_LICENSE_NUMBER_REGEX.test(licenseNumber)) {
      _.set(errors, fieldName, Messages.INVALID_NZ_DRIVER_LICENSE_NUMBER);
    }
    return errors;
  },

  validateNzPassportNumber: (values, fieldName) => {
    const errors = {};
    const passportNumber = _.get(values, fieldName);
    if (passportNumber && !NZ_PASSPORT_NUMBER_REGEX.test(passportNumber)) {
      _.set(errors, fieldName, Messages.INVALID_NZ_PASSPORT_NUMBER);
    }
    return errors;
  },

  validateTimeAtAddressFields: (values) => {
    const errors = {};
    const year = values.creditAssessment.yearsAtCurrentAddress;
    const months = values.creditAssessment.monthsAtCurrentAddress;
    if (!isEmptyOrZero(year) && (year < ValidRanges.MIN_YEARS_AT_ADDRESS || year > ValidRanges.MAX_YEARS_AT_ADDRESS)) {
      _.set(errors, 'creditAssessment.yearsAtCurrentAddress', Messages.INVALID_YEARS_AT_ADDRESS);
    }
    if (!isEmptyOrZero(months) &&
      (months < ValidRanges.MIN_MONTHS_AT_ADDRESS || months > ValidRanges.MAX_MONTHS_AT_ADDRESS)) {
      _.set(errors, 'creditAssessment.monthsAtCurrentAddress', Messages.INVALID_MONTHS_AT_ADDRESS);
    }
    if (isEmptyOrZero(year) && isEmptyOrZero(months)) {
      _.set(errors, 'creditAssessment.yearsAtCurrentAddress', Messages.REQUIRED_MESSAGE);
    }
    return errors;
  },

  isValidName: str => NAME_REGEX.test(str),

  hasUpperCaseCharacter: str => str.toLowerCase() !== str,

  containsNumber: str => /\d/i.test(str),

  containsOnlyNumbers: str => /^\d+$/.test(str),

  isValidPhoneNumber: str => /^[\d\s+\-()]+$/i.test(str),

  isValidEmailAddress: str => EMAIL_REGEX.test(str),

  isValidDay,

  isValidMonth,

  isValidYear
};

export default ValidationUtils;



/** WEBPACK FOOTER **
 ** ./src/common/validation/ValidationUtils.js
 **/